<template>
  <footer class="flex justify-center items-center bg-black text-white py-3 px-3 h-auto">
    <section class="flex text-center justify-between w-full">
      <!-- HOST ACTIONS -->
      <div class="inline-flex gap-4 items-center w-full" v-if="!sessionStore.userIsObserver">
        <!-- Open A/V Settings Modal-->
        <!--        <button-->
        <!--          data-cy="av-settings-toggle"-->
        <!--          class="flex flex-col items-center disabled:opacity-60 text-sm"-->
        <!--          :class="{-->
        <!--            'text-vice-teal-500': multiCameraStore.isOpenModal,-->
        <!--          }"-->
        <!--          @click="openAVSettingsModal"-->
        <!--          :disabled="sessionStore.mediaPermissionError || !multiCameraStore.devicesInitialized"-->
        <!--        >-->
        <!--          Audio/Video Settings-->
        <!--        </button>-->

        <InternetSpeedBox />
      </div>
      <!-- END OF HOST ACTIONS -->

      <!-- Center bar actions -->
      <div class="inline-flex gap-4 mx-2 justify-center items-center w-full step-av-controls">
        <template v-if="!sessionStore.userIsObserver">
          <!-- Participant default devices -->
          <div
            v-if="!sessionStore.isClinician && multiCameraStore.devicesInitialized"
            class="flex gap-x-2"
          >
            <!-- Camera selector -->
            <DefaultDeviceSelectorTile
              deviceIcon="/image/icons/main-camera.svg"
              :mediaType="MEDIA_TYPE.VIDEO"
              :deviceType="DEVICE_NAME.CAMERA"
              :deviceKind="DEVICES_KINDS.VIDEO_INPUT"
              :defaultDevices="[]"
              v-tippy="{
                content: isLiveConsultation
                  ? 'Video cannot be turned off in consultation phase'
                  : selectedCameraLabel,
                theme: 'light-border',
                interactive: true,
              }"
            />

            <!-- Microphone selector -->
            <DefaultAudioDeviceSelector :defaultDevices="{}" />

            <!-- Raise hand -->
            <ViceButton
              v-if="sessionStore.hasLivePhase"
              circle
              grey
              class="disabled:cursor-not-allowed w-12 h-12 transition-opacity"
              :class="{
                'bg-vice-teal-500': handRaised,
              }"
              @click="toggleUserRaiseHand"
              :disabled="!!isDisabledMediaControls"
              v-tippy="{
                content: (handRaised ? 'Lower' : 'Raise') + ' hand',
                theme: 'light-border',
                interactive: true,
              }"
            >
              <HandIcon :class="['w-10 h-10', handRaised ? 'text-yellow' : 'text-vice-teal-700']" />
            </ViceButton>
          </div>

          <!-- Clinician: Add Ex.Camera, Medical Devices, SmartGlasses, Smartphone -->
          <BottomBarActions
            v-if="showDevicesPanel"
            @openWifiConfigurationModal="showWifiConfigurationModal = true"
          />

          <!-- Screenshare Actions -->
          <ScreenShareActions v-if="shouldShowScreenShare" />

          <!-- Record actions -->
          <!-- <RecordActions /> -->
        </template>
      </div>

      <!-- Clinician default devices -->
      <div
        v-if="multiCameraStore.devicesInitialized && sessionStore.isClinician"
        class="flex gap-2 w-full justify-end pr-1"
      >
        <!-- Camera selector -->
        <DefaultDeviceSelectorTile
          deviceIcon="/image/icons/main-camera.svg"
          :mediaType="MEDIA_TYPE.VIDEO"
          :deviceType="DEVICE_NAME.CAMERA"
          :deviceKind="DEVICES_KINDS.VIDEO_INPUT"
          :defaultDevices="defaultDevices['clinician_devices']"
          v-tippy="{
            content: selectedCameraLabel ?? 'Select camera',
            theme: 'light-border',
            interactive: true,
          }"
        />

        <!-- Microphone and Speaker selector -->
        <DefaultAudioDeviceSelector :defaultDevices="defaultDevices['clinician_default_devices']" />

        <!-- Clinician end session -->
        <section v-if="showEndSessionByClinicianButton" class="flex justify-center items-center">
          <button
            data-cy="leave-session"
            @click="endSessionByClinician"
            v-tippy="{
              content: 'End session',
              theme: 'light-border',
              interactive: true,
            }"
          >
            <EndSessionIcon
              class="w-16 h-15"
              data-cy="leave-session"
              @click="endSessionByClinician"
            />
          </button>
        </section>
      </div>

      <!-- Student stop stream -->
      <div v-if="!sessionStore.isClinician" class="flex gap-2 w-full justify-end">
        <section
          v-if="shouldShowPutOnHoldButton"
          class="flex justify-center items-center rounded-md"
        >
          <template v-if="sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION">
            <ViceButton
              data-cy="session-on-hold"
              @click="togglePutOnHold(true)"
              v-if="!sessionStore.isPutOnHold && !sessionStore.userIsObserver"
              icon="ShieldExclamationIcon"
              mediumRed
              class="rounded-full"
              v-tippy="{
                content:
                  'You have 90 sec to turn your stream back ON. After 90 sec you will automatically leave the live consultation.',
                theme: 'light-border',
                interactive: true,
              }"
            >
              Turn stream OFF
            </ViceButton>

            <ViceButton
              data-cy="session-not-on-hold"
              dark
              class="rounded-full"
              :disabled="!turnOnAvailable"
              icon="ShieldCheckIcon"
              @click="togglePutOnHold(false)"
              v-if="sessionStore.isPutOnHold"
            >
              Turn stream ON
            </ViceButton>
          </template>
          <template v-else>
            <ViceButton
              data-cy="leave-session"
              mediumRed
              class="rounded-full"
              :loading="isLeaving"
              @click="leaveSession"
              v-if="!sessionStore.isPutOnHold"
            >
              <PhoneIcon
                class="w-6 h-5 transform"
                style="--tw-rotate: 45deg; --tw-scale-y: -1; --tw-translate-y: 2px"
              />
              Leave session
            </ViceButton>
          </template>
        </section>
      </div>
    </section>

    <ConnectDeviceModal
      :steps="smartglasses.steps"
      @close="StreamingDeviceStore.closeSmartGlassesModal"
      :open="StreamingDeviceStore.devices.smartglasses.qrModalOpen"
    ></ConnectDeviceModal>

    <ConnectDeviceModal
      :steps="smartphone.steps"
      @close="StreamingDeviceStore.closeSmartphoneModal"
      :open="StreamingDeviceStore.devices.smartphone.qrModalOpen"
    ></ConnectDeviceModal>

    <ConnectDeviceModal
      :steps="stethoscope.steps"
      @close="StreamingDeviceStore.closeStethoscopeModal"
      :open="StreamingDeviceStore.devices.stethoscope.qrModalOpen"
    ></ConnectDeviceModal>

    <!-- Modal for connecting external cameras -->
    <ConnectExternalDeviceModal
      data-cy="open-external-device-modal"
      @close="multiCameraStore.showPreviewModal = false"
      :open="multiCameraStore.showPreviewModal"
      v-if="multiCameraStore.showPreviewModal"
    />

    <WifiConfigurationModal
      @closeWifiConfigurationModal="showWifiConfigurationModal = false"
      :open="showWifiConfigurationModal"
    />

    <!-- MultiCamera AV Selection Modal -->
    <MultiAVSettingsModal />

    <!-- End session first Modal -->
    <EndSessionModal
      :open="isOpenEndSessionModal"
      @openEndSessionFinalModal="isOpenEndSessionFinalModal = true"
      @closeEndSessionModal="isOpenEndSessionModal = false"
    />

    <!-- End session second Modal -->
    <EndSessionFinalModal
      :open="isOpenEndSessionFinalModal"
      @endSession="endSession"
      @backToEndSessionModal="backToEndSessionModal"
      @closeEndSessionFinalModal="isOpenEndSessionFinalModal = false"
    />
  </footer>
</template>

<script>
import { ref, computed } from 'vue';
import { HandIcon, PhoneIcon } from '@heroicons/vue/solid';
import { hide } from '@intercom/messenger-js-sdk';
import { directive } from 'vue-tippy';
import ScreenShareActions from './ScreenShareActions.vue';
import ConnectDeviceModal from '@/Components/Modals/ConnectDeviceModal.vue';
import BottomBarActions from '@/Components/Session/Footer/BottomBarActions.vue';
import MultiAVSettingsModal from '@/Components/Modals/MultiAVSettingsModal.vue';
import WifiConfigurationModal from '@/Components/Modals/WifiConfigurationModal.vue';
import { stethoscope, smartglasses, smartphone } from '@/Utils/deviceConnectionSteps';
// import RecordActions from './RecordActions.vue';

import {
  VICE_SESSION_PHASES,
  CAMERA_TYPES,
  PHASE_STATUS,
  DEVICE_NAME,
  DEVICES_KINDS,
  MEDIA_TYPE,
} from '@/Utils/vice-constants';
import useSessionStore from '@/Stores/useSessionStore';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import useIOTDevicesStore from '@/Stores/useIOTDevicesStore';
import useJitsiScreenshare from '@/Composables/useJitsiScreenshare';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import EndSessionModal from '@/Components/Modals/EndSessionModal.vue';
import InternetSpeedBox from '@/Components/Session/Grid/InternetSpeedBox.vue';
import EndSessionFinalModal from '@/Components/Modals/EndSessionFinalModal.vue';
import ConnectExternalDeviceModal from '@/Components/Modals/ConnectExternalDeviceModal.vue';
import DefaultDeviceSelectorTile from '@/Components/Session/Grid/DefaultDeviceSelectorTile.vue';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light-border.css';
import EndSessionIcon from '@/Components/Icons/EndSessionIcon.vue';
import DefaultAudioDeviceSelector from '@/Components/Session/Grid/DefaultAudioDeviceSelector.vue';

export default {
  computed: {
    MEDIA_TYPE() {
      return MEDIA_TYPE;
    },
    DEVICES_KINDS() {
      return DEVICES_KINDS;
    },
    DEVICE_NAME() {
      return DEVICE_NAME;
    },
  },
  props: {
    jitsiRoom: {
      required: true,
    },
    defaultDevices: {
      type: Object,
      required: false,
    },
    turnOffSmartGlass: {
      type: Function,
      required: false,
    },
    endPhase: {
      type: Function,
    },
  },
  directives: {
    tippy: directive,
  },
  emits: ['handleRaiseMyHand'],
  components: {
    DefaultAudioDeviceSelector,
    PhoneIcon,
    EndSessionIcon,
    ViceButton,
    EndSessionModal,
    InternetSpeedBox,
    BottomBarActions,
    ScreenShareActions,
    ConnectDeviceModal,
    MultiAVSettingsModal,
    EndSessionFinalModal,
    WifiConfigurationModal,
    DefaultDeviceSelectorTile,
    ConnectExternalDeviceModal,
    HandIcon,
  },
  setup(props, { emit }) {
    const sessionStore = useSessionStore();
    const StreamingDeviceStore = useIOTDevicesStore();
    const multiCameraStore = useLocalStreamingDevicesStore();

    const { toggleScreenshare: secondToggleScreenShare } = useJitsiScreenshare(2);

    const isLeaving = ref(false);
    const cameraLoading = ref(false);
    const isOpenEndSessionModal = ref(false);
    const isOpenEndSessionFinalModal = ref(false);
    const showWifiConfigurationModal = ref(false);
    const userId = computed(() => sessionStore.authUser?.id);
    const handRaised = computed(() => sessionStore.isHandRaised(userId.value));
    const turnOnAvailable = computed(() => multiCameraStore.isMediaTrackReady);
    const selectedCameraLabel = computed(() => multiCameraStore.getSelectedActualCameraLabel());
    const selectedMicLabel = computed(() => multiCameraStore.getSelectedActualMicrophoneLabel());
    const selectedSpeakerLabel = computed(() => multiCameraStore.getSelectedActualSpeakersLabel());
    const isLiveConsultation = computed(
      () =>
        sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION && sessionStore.hasLivePhase,
    );
    const showEndSessionByClinicianButton = computed(
      () => sessionStore.hasLivePhase && sessionStore.isClinician,
    );
    const isDisabledMediaControls = computed(
      () =>
        !multiCameraStore.mediaActivated ||
        sessionStore.isPutOnHold ||
        (sessionStore.isPhasePaused && sessionStore.isClinician) ||
        !props.jitsiRoom ||
        sessionStore.mediaPermissionError,
    );

    const toggleUserMicrophone = () => {
      multiCameraStore.toggleMicrophone(CAMERA_TYPES.INTERNAL_WEBCAM);
    };

    const endSessionByClinician = () => {
      hide();
      showEndSessionModal();
    };

    const showEndSessionModal = () => {
      isOpenEndSessionModal.value = true;
    };

    const endSession = async () => {
      // Send consultation an end message to turn off smartglass before exit
      if (Number(sessionStore.currentPhase) === VICE_SESSION_PHASES.CONSULTATION) {
        props.turnOffSmartGlass();
      }
      // Forced end of the session models a Debrief end. Also adds the session to past sessions
      await props.endPhase({ id: 3 }, true);
    };

    const toggleUserCamera = async () => {
      cameraLoading.value = true;
      await multiCameraStore.toggleCamera(CAMERA_TYPES.INTERNAL_WEBCAM);
      cameraLoading.value = false;
    };

    const toggleUserRaiseHand = () => {
      emit('handleRaiseMyHand', {
        state: !handRaised.value,
        userId: userId.value,
        time: Date.now(),
      });
    };

    const openAVSettingsModal = () => {
      multiCameraStore.setCurrentActiveCameraType(CAMERA_TYPES.INTERNAL_WEBCAM);
      multiCameraStore.openAVSettingsModal();
    };

    const backToEndSessionModal = () => {
      isOpenEndSessionFinalModal.value = false;
      isOpenEndSessionModal.value = true;
    };

    const showDevicesPanel = computed(
      () =>
        sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION && sessionStore.isClinician,
    );

    const shouldShowPutOnHoldButton = computed(
      () => sessionStore.hasLivePhase && !sessionStore.isClinician,
    );

    const togglePutOnHold = (isOnHold) => {
      sessionStore.isPutOnHold = isOnHold;
    };

    const leaveSession = () => {
      isLeaving.value = true;
      multiCameraStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].jitsiRoom?.leave();
      window.location.replace(route('dashboard'));
    };

    // Show a screen icon on consultation when it is live
    const shouldShowScreenShare = computed(
      () => sessionStore.currentPhaseData.status === PHASE_STATUS.LIVE && sessionStore.isClinician,
    );

    return {
      endSession,
      leaveSession,
      togglePutOnHold,
      toggleUserCamera,
      toggleUserRaiseHand,
      openAVSettingsModal,
      toggleUserMicrophone,
      backToEndSessionModal,
      endSessionByClinician,
      secondToggleScreenShare,
      userId,
      isLeaving,
      handRaised,
      sessionStore,
      cameraLoading,
      turnOnAvailable,
      multiCameraStore,
      showDevicesPanel,
      selectedMicLabel,
      isLiveConsultation,
      selectedCameraLabel,
      selectedSpeakerLabel,
      StreamingDeviceStore,
      shouldShowScreenShare,
      isOpenEndSessionModal,
      isDisabledMediaControls,
      shouldShowPutOnHoldButton,
      isOpenEndSessionFinalModal,
      showWifiConfigurationModal,
      showEndSessionByClinicianButton,
      stethoscope,
      smartglasses,
      smartphone,
      CAMERA_TYPES,
      VICE_SESSION_PHASES,
    };
  },
};
</script>
